import React from 'react';

export function SettingsDataOutputTab({ p }) {
    return (
                <div className="settings-tab-content">
                    {p.dataSettingsLoading && (
                        <div className="loading">
                            <i className="fas fa-spinner fa-spin"></i> Loading data output settings...
                        </div>
                    )}

                    {/* Data Output Fields Section */}
                    <div className="settings-category" id="data-processing-fields-category">
                        <h3>
                            <i className="fas fa-table"></i> Data Output Fields
                            <span className="badge badge-config">CONFIG</span>
                        </h3>
                        <p className="section-description">
                            Manage data output field definitions. Each field defines a column that will be used when
                            processing and storing processed data.
                        </p>

                        {p.hasDataProcessingFieldChanges() && (
                            <div className="alert alert-warning">
                                <i className="fas fa-exclamation-triangle"></i>
                                <div>
                                    <strong>Warning:</strong> You have pending changes that have not been committed to
                                    the database.
                                    <ul style={{marginTop: '10px', marginBottom: '10px'}}>
                                        {p.pendingProcessingChanges.creates.length > 0 && (
                                            <li>{p.pendingProcessingChanges.creates.length} new
                                                field{p.pendingProcessingChanges.creates.length === 1 ? '' : 's'} to
                                                create</li>
                                        )}
                                        {p.pendingProcessingChanges.updates.length > 0 && (
                                            <li>{p.pendingProcessingChanges.updates.length} field{p.pendingProcessingChanges.updates.length === 1 ? '' : 's'} to
                                                update</li>
                                        )}
                                        {p.pendingProcessingChanges.deletes.length > 0 && (
                                            <li>{p.pendingProcessingChanges.deletes.length} field{p.pendingProcessingChanges.deletes.length === 1 ? '' : 's'} to
                                                delete</li>
                                        )}
                                    </ul>
                                    <strong>If you change the schema, older data will be moved to a historical table
                                        (YYMMDD_{'{schema}'}) and a new processing table will be created.</strong>
                                </div>
                            </div>
                        )}

                        {!p.hasDataProcessingFieldChanges() && (
                            <div className="alert alert-info">
                                <i className="fas fa-info-circle"></i>
                                <strong>Note:</strong> Changes are made locally first. Click "Update Database" to commit
                                all changes. If you change the schema, older data will be preserved in a historical
                                table.
                            </div>
                        )}

                        {p.dataProcessingFieldsLoading ? (
                            <div className="loading">
                                <i className="fas fa-spinner fa-spin"></i> Loading data output fields...
                            </div>
                        ) : (
                            <>
                                <div className="data-entries-actions">
                                    <button
                                        className="btn btn-primary"
                                        onClick={p.handleAddDataProcessingField}
                                        disabled={p.dataSettingsLoading || p.saving || p.dataProcessingFieldsLoading}
                                    >
                                        <i className="fas fa-plus"></i> Add Output Field
                                    </button>
                                </div>

                                {p.dataProcessingFields.length === 0 ? (
                                    <div className="empty-state">
                                        <i className="fas fa-inbox"></i>
                                        <p>No data output fields defined yet. Click "Add Output Field" to create
                                            one.</p>
                                    </div>
                                ) : (
                                    <div className="data-entries-list">
                                        {p.dataProcessingFields.map((field) => {
                                            const isPendingCreate = field.tempId !== undefined;
                                            const isPendingUpdate = p.pendingProcessingChanges.updates.some(u => u.id === field.id);
                                            const isPendingDelete = p.pendingProcessingChanges.deletes.includes(field.id);

                                            return (
                                                <div key={field.id || field.tempId}
                                                     className={`data-entry-card ${isPendingCreate ? 'pending-create' : ''} ${isPendingUpdate ? 'pending-update' : ''} ${isPendingDelete ? 'pending-delete' : ''}`}>
                                                    {isPendingCreate &&
                                                        <span className="pending-badge create">New (Pending)</span>}
                                                    {isPendingUpdate && <span className="pending-badge update">Modified (Pending)</span>}
                                                    {isPendingDelete &&
                                                        <span className="pending-badge delete">Deleted (Pending)</span>}
                                                    <div className="data-entry-header">
                                                        <h4>{field.visible_string}</h4>
                                                        <span className="data-entry-meta">
                            Column: <code>{field.column_name}</code> | Schema v{field.schema_version}
                          </span>
                                                    </div>
                                                    <div className="data-entry-actions">
                                                        {!isPendingDelete && (
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() => p.handleEditDataProcessingField(field)}
                                                                disabled={p.dataSettingsLoading || p.saving || p.dataProcessingFieldsLoading}
                                                            >
                                                                <i className="fas fa-edit"></i> Edit
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => p.handleDeleteDataProcessingFieldClick(field.id || field.tempId)}
                                                            disabled={p.dataSettingsLoading || p.saving || p.dataProcessingFieldsLoading}
                                                        >
                                                            <i className="fas fa-trash"></i> Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Data Output Field Editor Modal */}
                    {p.showDataProcessingFieldModal && (
                        <div className="modal show">
                            <div className="modal-content large">
                                <div className="modal-header">
                                    <h2 className="modal-title">
                                        {p.editingDataProcessingField ? 'Edit Output Field' : 'New Output Field'}
                                    </h2>
                                    <span className="close"
                                          onClick={() => p.setShowDataProcessingFieldModal(false)}>&times;</span>
                                </div>
                                <div className="modal-body">
                                    <div className="alert alert-warning">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <strong>Warning:</strong> Changes are saved locally. Click "Update Database" to
                                        commit. If you change the schema, older data will be preserved in a historical
                                        table.
                                    </div>

                                    <form>
                                        <div className="form-group">
                                            <label htmlFor="processingVisibleString">Visible String: <span
                                                className="required">*</span></label>
                                            <input
                                                type="text"
                                                id="processingVisibleString"
                                                value={p.dataProcessingFieldForm.visible_string}
                                                onChange={(e) => p.setDataProcessingFieldForm(prev => ({
                                                    ...prev,
                                                    visible_string: e.target.value
                                                }))}
                                                placeholder="e.g., Processed Content, Summary, Tags"
                                                required
                                            />
                                            <small>Human-readable name for this output field</small>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="processingColumnName">Column Name: <span
                                                className="required">*</span></label>
                                            <input
                                                type="text"
                                                id="processingColumnName"
                                                value={p.dataProcessingFieldForm.column_name}
                                                onChange={(e) => p.setDataProcessingFieldForm(prev => ({
                                                    ...prev,
                                                    column_name: e.target.value
                                                }))}
                                                placeholder="e.g., processed_content, summary, tags"
                                                required
                                            />
                                            <small>Database column name (must be valid SQL identifier - letters,
                                                numbers, underscore only). Can be edited for local changes, but will
                                                require schema migration when committed.</small>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="processingSchemaVersion">Schema Version:</label>
                                            <input
                                                type="number"
                                                id="processingSchemaVersion"
                                                value={p.dataProcessingFieldForm.schema_version}
                                                readOnly
                                                disabled={true}
                                                className="readonly-input"
                                            />
                                            <small>Schema version is automatically managed. It will be incremented from
                                                the database baseline when you commit schema changes.</small>
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary"
                                            onClick={() => p.setShowDataProcessingFieldModal(false)}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={p.handleSaveDataProcessingField}>
                                        {p.editingDataProcessingField ? 'Update' : 'Create'} Output Field
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Update Database Button Section */}
                    {p.hasDataProcessingFieldChanges() && (
                        <div className="settings-save-section"
                             style={{marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                            <button
                                className="btn btn-primary btn-save-settings"
                                onClick={() => p.setShowUpdateConfirm(true)}
                                disabled={p.saving || p.dataProcessingFieldsLoading}
                            >
                                <i className="fas fa-database"></i> {p.saving ? 'Updating...' : 'Update Database'}
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    p.loadDataProcessingFields();
                                    p.setMessage({type: 'info', text: 'All pending changes discarded'});
                                    setTimeout(() => p.setMessage({type: '', text: ''}), 3000);
                                }}
                                disabled={p.saving || p.dataProcessingFieldsLoading}
                                style={{marginLeft: '10px'}}
                            >
                                <i className="fas fa-undo"></i> Discard Changes
                            </button>
                        </div>
                    )}
                </div>
    );
}
